import { useEffect, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import {
  buildMarkdownPreviewSegments,
  resolveMermaidTheme,
  type MarkdownPreviewSegment
} from './markdownPreviewRender';
import { MermaidPreviewPopup } from './MermaidPreviewPopup';
import { renderMermaidSvg } from './mermaidRenderer';
import './markdownPreview.css';

export type MarkdownPreviewPaneProps = {
  markdown: string;
};

function MermaidInlineBlock({
  id,
  source,
  themeTick,
  onExpand
}: {
  id: string;
  source: string;
  themeTick: number;
  onExpand: (source: string) => void;
}) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSvg(null);
    setError(null);

    void (async () => {
      try {
        const next = await renderMermaidSvg(source, resolveMermaidTheme());
        if (!cancelled) setSvg(next);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not render Mermaid diagram.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, source, themeTick]);

  return (
    <div className="markdown-preview__mermaid">
      <Button
        icon="fullscreen"
        stylingMode="text"
        hint="Open diagram"
        elementAttr={{ class: 'markdown-preview__mermaid-expand', 'aria-label': 'Open diagram' }}
        onClick={() => onExpand(source)}
      />
      {error ? (
        <p className="markdown-preview__mermaid-error" role="alert">
          {error}
        </p>
      ) : svg ? (
        <div className="markdown-preview__mermaid-svg" dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <p className="markdown-preview__mermaid-loading">Rendering diagram…</p>
      )}
    </div>
  );
}

function PreviewSegmentView({
  segment,
  themeTick,
  onExpand
}: {
  segment: MarkdownPreviewSegment;
  themeTick: number;
  onExpand: (source: string) => void;
}) {
  if (segment.type === 'html') {
    if (!segment.html.trim()) return null;
    return (
      <div
        className="markdown-preview__html"
        dangerouslySetInnerHTML={{ __html: segment.html }}
      />
    );
  }

  return (
    <MermaidInlineBlock
      id={segment.id}
      source={segment.source}
      themeTick={themeTick}
      onExpand={onExpand}
    />
  );
}

export function MarkdownPreviewPane({ markdown }: MarkdownPreviewPaneProps) {
  const segments = useMemo(() => buildMarkdownPreviewSegments(markdown), [markdown]);
  const [popupSource, setPopupSource] = useState<string | null>(null);
  const [themeTick, setThemeTick] = useState(0);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.attributeName === 'data-theme')) {
        setThemeTick((value) => value + 1);
      }
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const empty = !markdown.trim();

  return (
    <div className="markdown-preview-pane">
      <div className="markdown-preview-pane__scroll">
        {empty ? (
          <p className="markdown-preview-pane__empty">Nothing to preview yet.</p>
        ) : (
          segments.map((segment, index) => (
            <PreviewSegmentView
              key={segment.type === 'mermaid' ? segment.id : `html-${index}`}
              segment={segment}
              themeTick={themeTick}
              onExpand={setPopupSource}
            />
          ))
        )}
      </div>
      <MermaidPreviewPopup
        visible={popupSource != null}
        source={popupSource}
        onClose={() => setPopupSource(null)}
      />
    </div>
  );
}
